import { Link, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const IMAGE_DELIVERY_BASE = "https://imagedelivery.net/WefzwPpyYAgz4nGrybaw0Q";
const FALLBACK_IMAGE = "/assets/img/explore-cites/explore-cites-bg-1.jpg";

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return `${baseUrl}${pathname}`;
}

function normalizeImagePath(image, fallback = FALLBACK_IMAGE) {
  if (!image) {
    return fallback;
  }

  if (/^https?:\/\//i.test(image) || image.startsWith("/")) {
    return image;
  }

  if (image.startsWith("assets/")) {
    return `/${image}`;
  }

  return `${IMAGE_DELIVERY_BASE}/${image.replace(/^\/+/, "")}`;
}

function formatPrice(property) {
  if (property?.price_on_application === "Yes") {
    return "Price on request";
  }

  const numericPrice = Number(String(property?.price || "").replace(/[^\d.]/g, ""));

  if (!numericPrice) {
    return "Price on request";
  }

  return `AED ${new Intl.NumberFormat("en-AE", { maximumFractionDigits: 0 }).format(numericPrice)}`;
}

function field(value, fallback = "-") {
  const cleaned = String(value || "").trim();
  return cleaned || fallback;
}

function formatLocation(property) {
  return [property?.sub_community, property?.community, property?.city].filter(Boolean).join(" - ") || "Dubai - UAE";
}

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-AE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function propertyReferenceFromRoute(propertyId, searchParams) {
  const queryReference = searchParams.get("id") || searchParams.get("ref") || searchParams.get("reference_number");
  return decodeURIComponent(queryReference || propertyId || "").trim();
}

function propertyImages(property) {
  const photos = Array.isArray(property?.photos4) ? property.photos4 : [];
  const images = photos.map((photo) => normalizeImagePath(photo.image)).filter(Boolean);
  return images.length ? [...new Set(images)] : [FALLBACK_IMAGE];
}

function cleanDescription(description) {
  const text = String(description || "")
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\r/g, "");

  return text
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function phoneUrl(property) {
  const phone = String(property?.agent_phone || "").replace(/[^\d+]/g, "");
  return phone ? `tel:${phone}` : "tel:+97144514747";
}

function whatsappUrl(property) {
  const rawPhone = property?.agent_whatsapp || property?.agent_phone || "";
  const phone = String(rawPhone).replace(/[^\d+]/g, "");

  if (!phone) {
    return "https://api.whatsapp.com/send?text=%20to%20";
  }

  return `https://api.whatsapp.com/send?phone=${encodeURIComponent(phone)}&text=%20to%20`;
}

function mapSource(property) {
  const geopoints = String(property?.geopoints || "").trim();
  const query = geopoints || formatLocation(property);
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=14&output=embed`;
}

function highlightItems(property) {
  return [
    ["Price", field(formatPrice(property)), "/assets/img/icon/property-single-icon1-1.svg"],
    ["Category", field(property.rsco), "/assets/img/icon/property-single-icon1-2.svg"],
    ["Bedrooms", field(property.bedrooms), "/assets/img/icon/property-single-icon1-4.svg"],
    ["Bath", field(property.No_of_Bathroom), "/assets/img/icon/property-single-icon1-5.svg"],
    ["Purpose", field(property.offering_type), "/assets/img/icon/property-single-icon1-7.svg"],
    ["Sqft", field(property.size), "/assets/img/icon/property-single-icon1-8.svg"],
    ["Parking", field(property.parking), "/assets/img/icon/property-single-icon1-9.svg"],
    ["Furnished", field(property.furnished), "/assets/img/icon/property-single-icon1-10.svg"],
    ["ID No.", field(property.reference_number), "/assets/img/icon/property-single-icon1-1.svg"],
    ["Unit Type", field(property.property_type), "/assets/img/icon/property-single-icon1-7.svg"],
    ["ORN", field(property.ORN), "/assets/img/icon/property-single-icon1-3.svg"],
    ["BRN", field(property.BRN), "/assets/img/icon/property-single-icon1-6.svg"],
  ];
}

function AgentCard({ property }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  function updateField(fieldName, value) {
    setForm((current) => ({
      ...current,
      [fieldName]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus("sending");
    setFeedback("");

    const payload = {
      proref: property.reference_number || property.id || property.title || "",
      agent_name: form.name,
      agent_phone: form.phone,
      agent_email: form.email,
      agent_address: form.message,
      current_url: window.location.href,
    };

    try {
      const response = await fetch(buildApiUrl("/api/EnquiryHandler.ashx"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Unable to send enquiry");
      }

      const result = await response.json();

      if (result.status !== "success") {
        throw new Error(result.message || "Unable to send enquiry");
      }

      setForm({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      setStatus("success");
      setFeedback(result.message || "Enquiry sent successfully");
    } catch (err) {
      setStatus("error");
      setFeedback(err.message || "Unable to send enquiry");
    }
  }

  return (
    <div className="widget sidebar-contact-form property-agent-widget">
      <h3 className="widget_title">Contact Agent</h3>
      <div className="property-agent-summary">
        <img src={normalizeImagePath(property.agent_img, "/assets/img/team/team_1_1.jpg")} alt={property.agent_name || "Agent"} />
        <div>
          <h4>{field(property.agent_name, "Blue Water Real Estate")}</h4>
          <p>{field(property.agent_designation, "Property Consultant")}</p>
        </div>
      </div>
      <div className="property-contact-actions">
        <a className="th-btn radius" href={phoneUrl(property)}>
          <i className="fa-solid fa-phone me-2" />
          Call
        </a>
        <a className="th-btn radius property-whatsapp-link" href={whatsappUrl(property)} target="_blank" rel="noreferrer">
          <i className="fab fa-whatsapp me-2" />
          WhatsApp
        </a>
      </div>
      <div className="contact-form-widget mt-4">
        <form className="newsletter-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              className="form-control"
              type="text"
              placeholder="Your Name"
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <input
              className="form-control"
              type="email"
              placeholder="Your Email"
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <input
              className="form-control"
              type="text"
              placeholder="Your Phone"
              value={form.phone}
              onChange={(event) => updateField("phone", event.target.value)}
              required
            />
          </div>
          <div className="form-group mb-4">
            <textarea
              name="message"
              cols="30"
              rows="3"
              className="form-control"
              placeholder="Your Message"
              value={form.message}
              onChange={(event) => updateField("message", event.target.value)}
            />
          </div>
          {feedback && <p className={`property-enquiry-feedback ${status === "success" ? "success" : "error"}`}>{feedback}</p>}
          <div className="form-btn pt-2">
            <button className="th-btn radius" type="submit" disabled={status === "sending"}>
              {status === "sending" ? "Sending..." : "Send Us"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const THUMBNAILS_PER_PAGE = 5;

function openImagePopup(images, startIndex) {
  const galleryItems = images.filter(Boolean).map((src) => ({ src }));
  const $ = window.jQuery || window.$;

  if (galleryItems.length && $?.magnificPopup?.open) {
    $.magnificPopup.open(
      {
        items: galleryItems,
        gallery: {
          enabled: true,
        },
        type: "image",
        mainClass: "mfp-zoom-in",
        removalDelay: 260,
      },
      startIndex
    );
    return;
  }

  if (galleryItems[startIndex]?.src) {
    window.location.href = galleryItems[startIndex].src;
  }
}

function PropertyGallery({ images, title }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const safeActiveIndex = Math.min(activeIndex, Math.max(images.length - 1, 0));
  const activeImage = images[safeActiveIndex] || FALLBACK_IMAGE;
  const maxThumbStart = Math.max(images.length - THUMBNAILS_PER_PAGE, 0);
  const visibleThumbnails = images.slice(thumbStart, thumbStart + THUMBNAILS_PER_PAGE);

  useEffect(() => {
    setActiveIndex(0);
    setThumbStart(0);
  }, [images]);

  function selectThumbnail(index) {
    setActiveIndex(index);

    if (index < thumbStart) {
      setThumbStart(index);
      return;
    }

    if (index >= thumbStart + THUMBNAILS_PER_PAGE) {
      setThumbStart(Math.min(index - THUMBNAILS_PER_PAGE + 1, maxThumbStart));
    }
  }

  function showPreviousThumbnails() {
    setThumbStart((current) => Math.max(current - 1, 0));
  }

  function showNextThumbnails() {
    setThumbStart((current) => Math.min(current + 1, maxThumbStart));
  }

  return (
    <div className="slider-area property-slider1 property-detail-gallery">
      <button type="button" className="property-detail-main-image" onClick={() => openImagePopup(images, safeActiveIndex)} aria-label="Open property image gallery">
        <div className="property-slider-img propery-single-slide">
          <img src={activeImage} alt={title || "Property"} />
        </div>
      </button>
      {images.length > 1 && (
        <div className="property-detail-thumb-slider-wrap">
          <button
            type="button"
            className="slider-arrow property-detail-thumb-arrow"
            onClick={showPreviousThumbnails}
            disabled={thumbStart === 0}
            aria-label="Previous property images"
          >
            <i className="far fa-arrow-left" />
          </button>
          <div className="property-detail-thumb-strip">
            {visibleThumbnails.map((image, offset) => {
              const imageIndex = thumbStart + offset;

              return (
                <button
                  type="button"
                  className={`property-detail-thumb${imageIndex === safeActiveIndex ? " active" : ""}`}
                  key={image}
                  onClick={() => selectThumbnail(imageIndex)}
                  aria-label={`Show property image ${imageIndex + 1}`}
                >
                  <img src={image} alt={title || "Property thumbnail"} />
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="slider-arrow property-detail-thumb-arrow"
            onClick={showNextThumbnails}
            disabled={thumbStart >= maxThumbStart}
            aria-label="Next property images"
          >
            <i className="far fa-arrow-right" />
          </button>
        </div>
      )}
    </div>
  );
}

function PropertyDetailsContent({ property }) {
  const images = useMemo(() => propertyImages(property), [property]);
  const description = cleanDescription(property.description_en);
  const amenities = Array.isArray(property.amenities) ? property.amenities : [];

  return (
    <section className="property-details space overflow-hidden">
      <div className="container">
        <div className="row gy-40 gx-50">
          <div className="col-xxl-8 col-lg-7">
            <div className="row gy-30">
              <PropertyGallery images={images} title={property.title} />
              <div className="property-page-single">
                <div className="page-content">
                  <div className="property-meta-wrap mb-55 fadeinup wow">
                    <div className="property-meta">
                      {property.featured && <span className="property-tag">{property.featured}</span>}
                      {property.last_update && (
                        <span>
                          <i className="fa-solid fa-calendar-days" /> {formatDate(property.last_update)}
                        </span>
                      )}
                      {property.offplan && (
                        <span>
                          <i className="fa-solid fa-building" /> {property.offplan}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="page-title-wrap fadeinup wow">
                    <h2 className="page-title mb-2">{property.title || "About This Property"}</h2>
                    <h4 className="page-title">{formatPrice(property)}</h4>
                  </div>
                  <div className="page-features fadeinup wow">
                    <div className="box-text">{formatLocation(property)}</div>
                    <ul className="property-featured">
                      <li>
                        <div className="icon">
                          <img src="/assets/img/icon/bed.svg" alt="bed" />
                        </div>
                        Bed {field(property.bedrooms)}
                      </li>
                      <li>
                        <div className="icon">
                          <img src="/assets/img/icon/bath.svg" alt="bath" />
                        </div>
                        Bath {field(property.No_of_Bathroom)}
                      </li>
                      <li>
                        <div className="icon">
                          <img src="/assets/img/icon/sqft.svg" alt="area" />
                        </div>
                        {field(property.size)} {field(property.sizeunits, "sqft")}
                      </li>
                    </ul>
                  </div>
                  <h3 className="page-title mt-50 mb-25 fadeinup wow">About This Property</h3>
                  {description.length > 0 ? (
                    description.map((paragraph) => (
                      <p className="mb-30 fadeinup wow" key={paragraph}>
                        {paragraph}
                      </p>
                    ))
                  ) : (
                    <p className="mb-30 fadeinup wow">Property details will be updated soon.</p>
                  )}
                  <div className="hightlighes-title-wrap mb-25 fadeinup wow">
                    <h2 className="page-title mb-2">Property Highlights</h2>
                    <h5 className="house-sell">{field(property.offering_type)}</h5>
                  </div>
                  <ul className="property-grid-list fadeinup wow">
                    {highlightItems(property).map(([label, value, icon]) => (
                      <li key={label}>
                        <div className="property-grid-list-icon">
                          <img src={icon} alt="" />
                        </div>
                        <div className="property-grid-list-details">
                          <h4 className="property-grid-list-title">{label}</h4>
                          <p className="property-grid-list-text">{value}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {amenities.length > 0 && (
                    <>
                      <h3 className="page-title mt-50 mb-25 fadeinup wow">Features & amenities</h3>
                      <div className="fea-anim-checklist fadeinup wow">
                        <div className="checklist style2 list-six-column">
                          <ul>
                            {amenities.map((amenity) => (
                              <li key={amenity.name}>{amenity.name}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </>
                  )}
                  <h3 className="page-title mt-50 mb-30 fadeinup wow">Location</h3>
                  <div className="location-map fadeinup wow">
                    <div className="contact-map">
                      <iframe src={mapSource(property)} title={`${property.title || "Property"} location`} loading="lazy" allowFullScreen />
                    </div>
                    <div className="location-map-address">
                      <div className="thumb">
                        <img src={images[0]} alt={property.title || "Property"} />
                      </div>
                      <div className="media-body">
                        <h4 className="title">Address:</h4>
                        <p className="text">{formatLocation(property)}</p>
                        <h4 className="title">Reference:</h4>
                        <p className="text">{field(property.reference_number || property.id)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xxl-4 col-lg-5">
            <aside className="sidebar-area">
              <AgentCard property={property} />
              <div className="widget widget_banner property-detail-banner" style={{ backgroundImage: "url(/assets/img/bg/widget_banner.jpg)" }}>
                <div className="widget-banner">
                  <h2 className="title">We can help you to find your next property</h2>
                  <Link to="/contact-us" className="th-btn radius">
                    Contact with Agent
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

function PropertyDetails() {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const referenceNumber = useMemo(() => propertyReferenceFromRoute(propertyId, searchParams), [propertyId, searchParams]);
  const [property, setProperty] = useState(null);
  const [status, setStatus] = useState(referenceNumber ? "loading" : "missing");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!referenceNumber) {
      setStatus("missing");
      return undefined;
    }

    const controller = new AbortController();

    async function loadProperty() {
      setStatus("loading");
      setError("");

      try {
        const response = await fetch(buildApiUrl("/api/propertydetail.ashx"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: referenceNumber }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Unable to load property details (${response.status})`);
        }

        const payload = await response.json();

        if (payload.result !== 1) {
          throw new Error(payload.message || "Unable to load property details");
        }

        const item = Array.isArray(payload.data) ? payload.data[0] : payload.data;

        if (!item) {
          throw new Error("Property details not found");
        }

        setProperty(item);
        setStatus("ready");
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }

        setProperty(null);
        setError(err.message || "Unable to load property details");
        setStatus("error");
      }
    }

    loadProperty();

    return () => controller.abort();
  }, [referenceNumber]);

  const title = property?.title || "Property Details";

  return (
    <>
      <div className="breadcumb-wrapper background-image" style={{ backgroundImage: "url(/assets/img/bg/breadcrumb-bg.jpg)" }}>
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">{title}</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>Property Details</li>
            </ul>
          </div>
        </div>
      </div>
      {status === "loading" && (
        <section className="property-details space">
          <div className="container text-center">Loading property details...</div>
        </section>
      )}
      {status === "missing" && (
        <section className="property-details space">
          <div className="container text-center">Property reference is missing.</div>
        </section>
      )}
      {status === "error" && (
        <section className="property-details space">
          <div className="container text-center">{error}</div>
        </section>
      )}
      {status === "ready" && property && <PropertyDetailsContent property={property} />}
    </>
  );
}

export default PropertyDetails;
